import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Paging from '../../component/Paging';
import request from '../../../utils/Request.utils';
import { formatYmd } from '../../../utils/Common.utils';
import { ToastContainer, toast } from 'react-toastify';
import Modal from '../../component/Modal';

const LIST_PAGE_SIZE = 10;
const formatNumber = (value) => Number(value || 0).toLocaleString();

const Inquiry = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currPage, setCurrPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const [loading, setLoading] = useState(true);

  // 선택된 항목
  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const successHandler = useCallback((response) => {
    if (response.messageCode === 200) {
      const data = response.data || {};
      setInquiries(data.dataList || []);
      setTotalPages(Number(data.tot_page) || 0);
      setTotalCnt(Number(data.totalCnt) || 0);
      setSelectedInquiries([]);
      setLoading(false);
    } else {
      toast.error("문의사항 정보를 불러올 수 없습니다.");
      setLoading(false);
    }
  }, []);

  const errorHandler = useCallback((error) => {
    toast.error("데이터를 불러오는 중 오류가 발생했습니다.");
    setLoading(false);
  }, []);

  const fetchInquiries = useCallback(() => {
    // 페이지를 옮기거나 다시 불러올 때는 표를 숨기지 않는다 — 숨겼다 다시 그리면 표·페이지 번호가 깜빡인다 (loading 은 첫 불러오기에만 true)
    const params = {
      curr_page: currPage,
      per_page: LIST_PAGE_SIZE,
      sort_col: 'reg_dtm',
      sort_by: 'desc',
    };
    request.get("/board/list/admin/BD_INQUIRY", params, successHandler, errorHandler);
  }, [currPage, successHandler, errorHandler]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries, currPage]);

  const handlePageChange = (page) => {
    setCurrPage(page);
  };

  const handleSelectInquiry = (inquiryId) => {
    setSelectedInquiries(prevSelectedInquiries =>
      prevSelectedInquiries.includes(inquiryId)
        ? prevSelectedInquiries.filter(id => id !== inquiryId)
        : [...prevSelectedInquiries, inquiryId]
    );
  };

  const handleSelectAll = () => {
    if (inquiries.length === 0) {
      setSelectedInquiries([]);
      return;
    }

    if (selectedInquiries.length === inquiries.length) {
      setSelectedInquiries([]);
    } else {
      setSelectedInquiries(inquiries.map(inquiry => inquiry.board_seq));
    }
  };

  const openModal = () => {
    if (selectedInquiries.length === 0) {
      toast.error("삭제할 문의사항을 선택해 주세요.");
      return;
    }
    setModalIsOpen(true);
  };
  const closeModal = () => setModalIsOpen(false);

  const handleDelete = async () => {
    if (selectedInquiries.length === 0) {
      toast.error("삭제할 문의사항을 선택해 주세요.");
      closeModal();
      return;
    }

    try {
      await request.del("/board/BD_INQUIRY", { board_seqs: selectedInquiries }, () => {
        toast.success("문의사항이 성공적으로 삭제되었습니다.");
        fetchInquiries();
        setSelectedInquiries([]);
      }, () => {
        toast.error("문의사항 삭제 중 오류가 발생했습니다.");
      });
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">1:1 문의 관리</h2>
      <div className="adm_com_pg_content">
        <div className="adm_com_btn_wrap">
          <button className={`adm_com_btn ${selectedInquiries.length > 0 ? "blue" : "gray"}`} onClick={openModal} disabled={selectedInquiries.length === 0}>선택삭제</button>
        </div>
        {loading ? null : (
          <table className='adm_com_board_list'>
            <colgroup>
              <col width="5%" />
              <col width="5%" />
              <col width="35%" />
              <col width="10%" />
              <col width="15%" />
              <col width="15%" />
              <col width="5%" />
              <col width="10%" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="check">
                    <input
                      type="checkbox"
                      id="chk01"
                      className="adm_com_chk"
                      onChange={handleSelectAll}
                      checked={inquiries.length > 0 && selectedInquiries.length === inquiries.length}
                      disabled={inquiries.length === 0}
                    />
                    <label htmlFor="chk01" />
                  </div>
                </th>
                <th>번호</th>
                <th>제목</th>
                <th>답변여부</th>
                <th>작성자</th>
                <th>작성일자</th>
                <th>조회수</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.length === 0 && (
                <tr>
                  <td colSpan={8}>등록된 1:1 문의가 없습니다.</td>
                </tr>
              )}
              {inquiries.map((inquiry, index) => (
                <tr key={inquiry.board_seq}>
                  <td>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={`chk${index + 2}`}
                        className="adm_com_chk"
                        checked={selectedInquiries.includes(inquiry.board_seq)}
                        onChange={() => handleSelectInquiry(inquiry.board_seq)}
                      />
                      <label htmlFor={`chk${index + 2}`} />
                    </div>
                  </td>
                  <td>{formatNumber(totalCnt - ((currPage - 1) * LIST_PAGE_SIZE + index))}</td>
                  <td className="notice_title_cell">
                    <span className="board_list_title" title={inquiry.board_title}>{inquiry.board_title}</span>
                  </td>
                  <td className={inquiry.board_content2?.trim() ? 'td_green bold' : 'td_red bold'}>
                    {inquiry.board_content2?.trim() ? 'Y' : 'X'}
                  </td>
                  <td className="td_ellipsis" title={inquiry.owner_seq_al_user?.user_id || '관리자'}>
                    {inquiry.owner_seq_al_user?.user_id || '관리자'}
                  </td>
                  <td>{formatYmd(inquiry.reg_dtm)}</td>
                  <td>{formatNumber(inquiry.board_hit)}</td>
                  <td className="board_action_cell">
                    <button onClick={() => navigate(`/Manage/Inquiry/Write/${inquiry.board_seq}`)} className="adm_com_btn blue line">답변달기</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {totalPages > 0 && <Paging currentPage={currPage} totalPages={totalPages} onPageChange={handlePageChange} />}
      </div>
      <Modal
        open={modalIsOpen}
        close={closeModal}
        type="confirm"
        popSize="s"
        title="삭제 확인"
        description="선택된 문의사항을 삭제하시겠습니까?"
      >
        <div className="btn_container">
          <button className="adm_com_btn blue" onClick={handleDelete}>삭제</button>
          <button className="adm_com_btn gray" onClick={closeModal}>취소</button>
        </div>
      </Modal>

      <ToastContainer />
    </main>
  );
};

export default Inquiry;
